// components/entities/UnitRenderer.tsx
import React, { useCallback } from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import clsx from 'clsx';
import { Box, Tag, Maximize2, Banknote, MapPin, Store, Coffee, Warehouse, Building2 } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import { BaseEntityProps, EntityRowAction, LeasingUnitDTO, UnitStatus, UnitType } from '#types/types';
import { EntitySkeleton } from './EntityView';
import styles from '../portal/Table.module.css';
import type { TableColumnDef } from '../portal/Table';
import { EntityRowActions } from './EntityRowActions';
import { TableSelectionCheckbox } from '../portal/TableSelectionCheckbox';
import { useTableGlass, TABLE_GLASS_ROW } from '../portal/tableSurface';

const getUnitTypeIcon = (type: UnitType) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- pre-existing switch; the default branch is the intended catch-all
  switch (type) {
    case 'RETAIL': return <Store size={14} />;
    case 'RESTAURANT': return <Coffee size={14} />;
    case 'STORAGE': return <Warehouse size={14} />;
    case 'OFFICE': return <Building2 size={14} />;
    default: return <Box size={14} />;
  }
};

const getStatusStyles = (status: UnitStatus | null) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- pre-existing switch; the default branch is the intended catch-all
  switch (status) {
    case 'AVAILABLE': return 'bg-[#4DAB9A]/20 text-[#4DAB9A]';
    case 'UNDER_OFFER': return 'bg-[#FFA344]/20 text-[#FFA344]';
    case 'LEASED': return 'bg-[#9A6DD7]/20 text-[#9A6DD7]';
    case 'EXPIRING_SOON': return 'bg-[#FF7369]/20 text-[#FF7369]';
    default: return 'bg-[#979A9B]/15 text-[#979A9B]';
  }
};

const DEFAULT_ROW_COLUMN_ORDER = ['unit', 'property', 'floor', 'occupant', 'type', 'surface', 'rent', 'status', 'actions'] as const;

// Frozen per-alignment cell styles: the row renders one cell per column, so a literal
// object here would allocate once per cell of every render of every row.
const CELL_STYLE_START: React.CSSProperties = { justifyContent: 'flex-start' };
const CELL_STYLE_CENTER: React.CSSProperties = { justifyContent: 'center' };
const CELL_STYLE_END: React.CSSProperties = { justifyContent: 'flex-end' };
const CELL_STYLE_SELECTION: React.CSSProperties = { justifyContent: 'center', padding: '0 8px' };

const resolveCellStyle = (column: TableColumnDef): React.CSSProperties => {
  if (column.id === '_selection') return CELL_STYLE_SELECTION;
  if (column.align === 'right') return CELL_STYLE_END;
  if (column.align === 'center') return CELL_STYLE_CENTER;
  return CELL_STYLE_START;
};

export interface UnitRendererProps extends BaseEntityProps<LeasingUnitDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingUnitDTO>[];
}

export const UnitRenderer = React.memo(({
  entityId,
  variant = 'card',
  onClick,
  className,
  columns,
  rowActions = [],
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
  onSelectionZoneMouseDown,
}: UnitRendererProps) => {
  const glass = useTableGlass();
  const unit = useKernelStore((state) => state.inventory.Unit.registry[entityId]);
  const property = useKernelStore((state) => (unit?.propertyId ? state.inventory.Property.registry[unit.propertyId] : null));
  const floorRegistry = useKernelStore((state) => state.inventory.Floor.registry);
  const activeDeal = useKernelStore((state) => (unit?.activeDealId ? state.inventory.Deal.registry[unit.activeDealId] : null));
  const brand = useKernelStore((state) => (activeDeal?.brandId ? state.inventory.Brand.registry[activeDeal.brandId] : null));
  const handleSelect = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) onClick();
  }, [onClick]);
  const handleSelectionClick = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);
  const handleSelectionMouseDown = useCallback(
    (e: React.MouseEvent) => onSelectionZoneMouseDown?.(e, entityId),
    [onSelectionZoneMouseDown, entityId],
  );
  const handleToggleSelect = useCallback(() => onToggleSelect?.(entityId), [onToggleSelect, entityId]);

  if (!unit) return <EntitySkeleton variant={variant} className={className} />;

  if (variant === 'row') {
    const cellRenderers: Record<string, React.ReactNode> = {
      _selection: isSelectable ? (
        <div
          className="flex h-full items-center justify-center"
          onClick={handleSelectionClick}
          onMouseDown={handleSelectionMouseDown}
        >
          <TableSelectionCheckbox
            checked={isSelected}
            onChange={handleToggleSelect}
            className={clsx(
              'transition-opacity',
              isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          />
        </div>
      ) : null,
      unit: (
        <div className="flex min-w-0 flex-col justify-center">
          <div className="flex items-center gap-2 truncate text-[13px] font-semibold text-white">
            <span className="text-white/40">{getUnitTypeIcon(unit.type)}</span>
            {unit.unitIdentifier}
          </div>
        </div>
      ),
      occupant: (
        <div className="flex min-w-0 items-center gap-3">
          {brand?.logoUrl ? (
            <BrandMark logoUrl={brand.logoUrl} name={brand?.name} size={28} radius={5} />
          ) : (
            <div className="flex h-7 w-7 flex-none items-center justify-center overflow-hidden rounded border border-white/5 bg-white/5">
              <Tag size={14} className="text-white/10" />
            </div>
          )}
          <div className="min-w-0">
            <div className={clsx('truncate text-[12px] font-medium', brand ? 'text-white/80' : 'text-white/20 italic')}>
              {brand?.name || (unit.status === 'AVAILABLE' ? 'Vacant' : 'No Brand')}
            </div>
            {activeDeal && (
              <div className="text-[9px] text-white/30">
                Deal: {activeDeal.status}
              </div>
            )}
          </div>
        </div>
      ),
      property: (
        <div className="flex items-center gap-1 truncate text-[12px] text-white/70">
          <MapPin size={12} className="text-white/30" /> {property?.name || '...'}
        </div>
      ),
      floor: (
        <div className="truncate text-[12px] text-white/70">
          {(() => {
            const floorIds = unit.floorIds || [];
            if (floorIds.length === 0) return '-';
            const firstFloorId = floorIds[0];
            if (!firstFloorId) return '-';
            const firstFloor = floorRegistry[firstFloorId];
            if (!firstFloor) return '-';
            if (floorIds.length === 1) return firstFloor.name;
            return `${firstFloor.name} +${floorIds.length - 1}`;
          })()}
        </div>
      ),
      type: (
        <span className="rounded border border-white/5 bg-white/5 px-2 py-0.5 text-[11px] text-white/50">
          {unit.type}
        </span>
      ),
      surface: (
        <div className="flex items-center gap-2 font-mono text-[12px] text-white/70">
          <Maximize2 size={12} className="text-white/20" />
          {unit.areaSqm ? Math.round(unit.areaSqm) : '-'} <span className="text-[10px] text-white/30">m2</span>
        </div>
      ),
      rent: (
        <div className="flex items-center gap-2 font-mono text-[12px] text-white/70">
          <Banknote size={12} className="text-white/20" />
          {unit.askingRent ? unit.askingRent.toLocaleString() : '-'} <span className="text-[10px] text-white/30">{unit.askingRentCurrency || 'EUR'}</span>
        </div>
      ),
      status: (
        <span className={clsx('rounded-full px-2 py-0.5 text-[9px] font-medium capitalize', getStatusStyles(unit.status))}>
          {unit.status?.replace('_', ' ').toLowerCase()}
        </span>
      ),
      actions: (
        <div className="flex h-full items-center justify-end">
          <EntityRowActions entityType="Unit" entity={unit} rowActions={rowActions} />
        </div>
      ),
    };

    const orderedColumns: TableColumnDef[] = columns ?? DEFAULT_ROW_COLUMN_ORDER.map((id) => ({ id, header: '', minWidth: 1 }));

    return (
      <div
        className={clsx(
          styles.gridRow,
          styles.bodyRow,
          'group',
          glass && !isSelected && TABLE_GLASS_ROW,
          isSelected ? '!bg-[#0A84FF14] hover:!bg-[#0A84FF22]' : undefined,
          className,
        )}
        onClick={handleSelect}
      >
        {orderedColumns.map((column) => (
          <div
            key={column.id}
            className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
            style={resolveCellStyle(column)}
          >
            {cellRenderers[column.id] || null}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      onClick={handleSelect}
      className={clsx(
        'group relative flex cursor-pointer flex-col gap-2 rounded-xl border border-[#2A2A2A] bg-[#141414] p-3 transition-all hover:border-white/20',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-white/40 transition-colors group-hover:text-white/80">
          {getUnitTypeIcon(unit.type)}
        </div>
        <span className={clsx('rounded-full px-2 py-0.5 text-[8px] font-medium capitalize', getStatusStyles(unit.status))}>
          {unit.status?.replace('_', ' ').toLowerCase()}
        </span>
      </div>
      <div>
        <div className="text-sm font-bold text-white/90">{unit.unitIdentifier}</div>
        <div className="truncate text-[10px] text-white/40">{property?.name}</div>
      </div>
      <div className="mt-1 flex items-center justify-between border-t border-white/5 pt-2">
        <div className="font-mono text-[11px] text-white/60">{unit.areaSqm || 0} m2</div>
        <div className="font-mono text-[11px] text-[#4DAB9A]">{unit.askingRent?.toLocaleString() || '-'} EUR</div>
      </div>
    </div>
  );
});

UnitRenderer.displayName = 'UnitRenderer';
